/**
 * Hook React pour gérer les notifications de nouvelles commandes en temps réel
 * WebSocket avec fallback polling intelligent
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getWebSocketBaseUrl } from '../config/api.config';
import apiService from '../services/api';
import audioService from '../services/audioService';
import { playNotificationFromPrefs } from '../utils/notificationSoundPrefs';
import {
  ensureAssistanceSessionFresh,
  peekAssistanceSession,
} from '../utils/assistanceSession';
import {
  buildPendingCountQueries,
  sumPendingCountTotals,
} from '../utils/commandeStatuts';

const getWebSocketUrl = () => getWebSocketBaseUrl();

const isNotificationCommandeWeb = (data = {}) => {
  const canal = String(data?.canalVente || '').toUpperCase();
  if (canal === 'POS') return false;
  if (data?.modeRetrait === 'comptoir') return false;
  return true;
};

function playMerchantNotificationSound(commercantId, getCurrentUser) {
  const currentUser = getCurrentUser();
  const userCommercantId = currentUser?.commercantId || currentUser?.commercant?.id || null;
  const finalCommercantId = commercantId || userCommercantId;
  playNotificationFromPrefs(audioService, finalCommercantId);
}

/**
 * Hook pour écouter les nouvelles commandes en temps réel
 */
export function useCommandesWebSocket(
  commercantId = null,
  enabled = true,
  onNewCommande = null,
  onNewRendezVous = null,
  playSound = true
) {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastNotification, setLastNotification] = useState(null);

  const pollingIntervalRef = useRef(null);
  const lastPollTimeRef = useRef(0);
  const socketConnectedRef = useRef(false);

  const getCurrentUser = useCallback(() => {
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (err) {
      console.error('Erreur parsing user data:', err);
    }
    return null;
  }, []);

  const fetchPendingCount = useCallback(async () => {
    try {
      const currentUser = getCurrentUser();
      const isCommercant = currentUser?.role === 'COMMERCANT';
      const userCommercantId = currentUser?.commercantId || currentUser?.commercant?.id || null;
      const finalCommercantId = commercantId || userCommercantId;

      if (isCommercant && !finalCommercantId) {
        return;
      }

      // Requêtes séparées COMMANDE (historique) + RENDEZ_VOUS (badge) — jamais un fetch sans type.
      const queries = buildPendingCountQueries({ commercantId: finalCommercantId });
      const results = await Promise.all(
        queries.map((params) => apiService.getCommandes(params))
      );

      setPendingCount(sumPendingCountTotals(results));
      lastPollTimeRef.current = Date.now();
    } catch (err) {
      console.error('[useCommandesWebSocket] Erreur fetch pending count:', err);
    }
  }, [commercantId, getCurrentUser]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Hors-WS : 7 requêtes badge / tick. 5s ≈ 1260/15min (dépassait GLOBAL 1000).
    // 15s ≈ 420/15min — marge pour navigation + autres polls sous plafond GLOBAL.
    const interval = socketConnectedRef.current ? 60000 : 15000;
    pollingIntervalRef.current = setInterval(() => {
      fetchPendingCount();
    }, interval);
    fetchPendingCount();
  }, [fetchPendingCount]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const handleNewCommande = useCallback((data) => {
    if (!isNotificationCommandeWeb(data)) {
      console.debug('[useCommandesWebSocket] Ignoré (vente caisse POS, pas de son)');
      return;
    }
    console.log('[useCommandesWebSocket] Nouvelle commande reçue:', data);
    setLastNotification({ type: 'commande', data, timestamp: Date.now() });
    if (playSound) {
      playMerchantNotificationSound(commercantId, getCurrentUser);
    }
    fetchPendingCount();
    if (onNewCommande) {
      onNewCommande(data);
    }
  }, [onNewCommande, commercantId, getCurrentUser, fetchPendingCount, playSound]);

  const handleNewRendezVous = useCallback((data) => {
    console.log('[useCommandesWebSocket] Nouveau rendez-vous reçu:', data);
    setLastNotification({ type: 'rendezvous', data, timestamp: Date.now() });
    if (playSound) {
      playMerchantNotificationSound(commercantId, getCurrentUser);
    }
    fetchPendingCount();
    if (onNewRendezVous) {
      onNewRendezVous(data);
    }
  }, [onNewRendezVous, commercantId, getCurrentUser, fetchPendingCount, playSound]);

  const handleNewTableReservation = useCallback((data) => {
    console.log('[useCommandesWebSocket] Nouvelle réservation table reçue:', data);
    setLastNotification({ type: 'reservation-table', data, timestamp: Date.now() });
    if (playSound) {
      playMerchantNotificationSound(commercantId, getCurrentUser);
    }
    window.dispatchEvent(new CustomEvent('clickorico:refresh-table-reservations'));
    if (onNewRendezVous) {
      onNewRendezVous(data);
    }
  }, [onNewRendezVous, commercantId, getCurrentUser, playSound]);

  const handleCommandeStatut = useCallback((data) => {
    window.dispatchEvent(
      new CustomEvent('clickorico:refresh-commandes-list', { detail: data || {} })
    );
    fetchPendingCount();
  }, [fetchPendingCount]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const currentUser = getCurrentUser();
    if (!currentUser?.id) {
      console.warn('[useCommandesWebSocket] Utilisateur non trouvé');
      return undefined;
    }

    const socket = io(getWebSocketUrl(), {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[useCommandesWebSocket] Connecté');
      setConnected(true);
      setError(null);
      socketConnectedRef.current = true;

      // A1.2 — si assistance expirée : aucun auth WS avant nettoyage UI.
      const peeked = peekAssistanceSession();
      if (peeked && !ensureAssistanceSessionFresh()) {
        setError('Session assistance expirée');
        setConnected(false);
        socketConnectedRef.current = false;
        return;
      }

      const token = apiService.getAccessToken() || apiService.getSuperAdminAccessToken();
      socket.emit('commande:authenticate', {
        userId: currentUser.id,
        token,
      });
      startPolling();
    });

    socket.on('commande:authenticated', () => {
      setConnected(true);
      setError(null);
    });

    socket.on('commande:auth_error', (authError) => {
      console.error('[useCommandesWebSocket] Erreur authentification:', authError);
      setError(authError.message || 'Erreur d\'authentification');
      setConnected(false);
      socketConnectedRef.current = false;
      startPolling();
    });

    socket.on('commande:new', handleNewCommande);
    socket.on('commande:statut', handleCommandeStatut);
    socket.on('rendezvous:new', handleNewRendezVous);
    socket.on('reservation-table:new', handleNewTableReservation);

    socket.on('disconnect', () => {
      setConnected(false);
      socketConnectedRef.current = false;
      startPolling();
    });

    socket.on('connect_error', (err) => {
      console.error('[useCommandesWebSocket] Erreur connexion:', err);
      setError('Erreur de connexion WebSocket');
      setConnected(false);
      socketConnectedRef.current = false;
      startPolling();
    });

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
        pollingIntervalRef.current = setInterval(() => {
          fetchPendingCount();
        }, 20000);
      } else {
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
      if (socket.connected) {
        socket.disconnect();
      }
      socketRef.current = null;
    };
  }, [
    enabled,
    commercantId,
    getCurrentUser,
    handleNewCommande,
    handleCommandeStatut,
    handleNewRendezVous,
    handleNewTableReservation,
    startPolling,
    stopPolling,
    fetchPendingCount,
  ]);

  useEffect(() => {
    const handleFocus = () => {
      fetchPendingCount();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchPendingCount]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchPendingCount();
    };
    window.addEventListener('clickorico:refresh-commandes-pending-count', handleRefresh);
    return () => window.removeEventListener('clickorico:refresh-commandes-pending-count', handleRefresh);
  }, [fetchPendingCount]);

  return {
    connected,
    error,
    pendingCount,
    lastNotification,
    refreshCount: fetchPendingCount,
  };
}
